
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Trash2 as TrashIcon, 
  Pencil as EditIcon,
  Mail as MailIcon,
  ExternalLink as ExchangeIcon,
  Server as ImapIcon
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface EmailAccountItemProps {
  account: any;
  onEdit: (account: any) => void;
  onDelete: (account: any) => void;
}

export const EmailAccountItem = ({ account, onEdit, onDelete }: EmailAccountItemProps) => {
  const getProviderIcon = () => {
    switch (account.provider) {
      case 'gmail':
        return <MailIcon className="h-5 w-5 text-red-500" />;
      case 'exchange':
        return <ExchangeIcon className="h-5 w-5 text-blue-500" />;
      case 'imap':
        return <ImapIcon className="h-5 w-5 text-green-500" />;
      default:
        return <MailIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getProviderName = () => {
    switch (account.provider) {
      case 'gmail':
        return 'Gmail';
      case 'exchange':
        return 'Exchange';
      case 'imap':
        return 'IMAP/SMTP';
      default:
        return 'Email';
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getProviderIcon()}
            <div>
              <p className="font-medium">{account.name}</p>
              <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                <span>{account.email}</span>
                <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                  {getProviderName()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex space-x-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(account)}
                >
                  <EditIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit account</TooltipContent>
            </Tooltip>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon">
                  <TrashIcon className="h-4 w-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Email Account</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove this email account? This will disconnect the account from your profile.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(account)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
